import {
  BILLING_MODELS,
  type BillingModel,
  type Cadence,
  type EngagementSummary,
} from '@clockwise/clients/sows';
import {
  compareMoney,
  formatDollars,
  isMoney,
  percentOfMoney,
  subtractMoney,
  sumMoney,
  toMoney,
} from '@clockwise/shared';

export const BILLING_MODEL_LABELS: Record<BillingModel, string> = {
  retainer: 'Retainer',
  tm: 'T&M',
  metrics: 'Metrics',
};

export const ENGAGEMENT_CADENCE_LABELS: Record<Cadence, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semi_annual: 'Semi-Annual',
  annual: 'Annual',
  one_time: 'One-Time',
};

// Shared with the API's SOW audit narrator, so a fee reads the same on the
// card and in its audit trail. Re-exported for existing imports.
export { formatDollars };

/**
 * What a retainer bills per period after its discount, as a money string — or
 * `null` when the stored fee is not a number.
 *
 * Mirrors `priceDiscount` in the API's invoice pricing (apps/api/src/core/
 * invoices/generation/pricing.ts), so the card shows what the invoice bills:
 * a percentage comes off the fee, and a fixed discount is capped at the fee
 * rather than turning the retainer into a credit.
 */
function netRetainerFee(e: Pick<EngagementSummary, 'fee' | 'discount'>): string | null {
  if (!isMoney(e.fee)) return null;
  const d = e.discount;
  if (!d || d.amount <= 0) return toMoney(e.fee);
  const off =
    d.kind === 'percentage'
      ? percentOfMoney(e.fee, String(d.amount))
      : compareMoney(String(d.amount), e.fee) > 0
        ? e.fee
        : String(d.amount);
  return subtractMoney(e.fee, off);
}

/**
 * A SOW's engagements in two words: which billing models it bills under, and
 * what its retainers charge per period after discount. `null` for either
 * means there is nothing to say (no engagements; no retainer).
 *
 * Retainer fees are summed only when every retainer bills on the same cadence —
 * $1,000 monthly plus $12,000 annually has no honest total, so a mix reads as a
 * count instead. A retainer naming no cadence inherits the customer's, passed
 * as `inheritedCadence` already mapped into the engagement vocabulary (the
 * customer's `yearly` is an engagement's `annual`), so the two compare equal.
 * `'unknown'` means the customer could not be read: an inheriting retainer
 * then says so rather than printing a bare fee.
 */
export function summarizeEngagements(
  engagements: readonly Pick<
    EngagementSummary,
    'billingModel' | 'fee' | 'discount' | 'billingCadence'
  >[],
  inheritedCadence: Cadence | null | 'unknown',
): { model: string | null; fee: string | null } {
  const models = BILLING_MODELS.filter((m) => engagements.some((e) => e.billingModel === m));
  const model = models.length > 0 ? models.map((m) => BILLING_MODEL_LABELS[m]).join(' + ') : null;

  const retainers = engagements.filter((e) => e.billingModel === 'retainer');
  if (retainers.length === 0) return { model, fee: null };

  const count = retainers.length === 1 ? '1 retainer' : `${retainers.length} retainers`;
  const cadences = new Set(retainers.map((e) => e.billingCadence ?? inheritedCadence));
  const fees = retainers.map(netRetainerFee);
  // A retainer always has a fee (the server's shape CHECK), but a blank one
  // must not sum as a silently-invented $0.
  if (cadences.size > 1 || fees.some((f) => f === null)) return { model, fee: count };

  const total = formatDollars(Number(sumMoney(fees as string[])));
  const [cadence] = cadences;
  if (cadence === 'unknown') return { model, fee: `${total} / Unknown cadence` };
  return { model, fee: cadence ? `${total} / ${ENGAGEMENT_CADENCE_LABELS[cadence]}` : total };
}
